import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Eye, EyeOff } from 'lucide-react';
import { GoogleSignInButton, AuthDivider } from '../components/GoogleSignInButton';

const LoginPage = () => {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState('error');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectFrom = location.state?.from;
  const redirectMessage = location.state?.message;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setErrorType('error');

    try {
      const user = await login(phone, password, remember);
      if (user.role === 'admin' || user.role === 'employee') {
        navigate('/admin');
      } else if (redirectFrom) {
        // Honor the page that bounced the user here (e.g. /checkout)
        navigate(redirectFrom, { replace: true });
      } else {
        navigate('/compte');
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 403) {
        setErrorType('pending');
        setError(typeof detail === 'string' ? detail : t('login_error'));
      } else if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg || e).join(' '));
      } else if (!error.response) {
        setError('Erreur de connexion au serveur');
      } else {
        setError(t('login_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-[color:var(--color-bg)] flex items-center justify-center px-4 relative"
    >
      <div className="absolute inset-0 bg-sand overflow-hidden pointer-events-none"><div className="absolute -left-32 -top-32 w-[28rem] h-[28rem] rounded-full bg-[color:var(--color-primary)] opacity-10" /><div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-[color:var(--color-emerald)] opacity-10" /></div>
      
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-[color:rgb(var(--surface-rgb)/0.92)] backdrop-blur-xl border border-white/10 p-8 rounded-3xl shadow-xl">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/">
              <span className="text-3xl font-bold tracking-tight text-[color:var(--color-primary)]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                <span style={{ color: 'var(--color-logo-gold)' }}>Ô'</span>Delicieuse <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[color:var(--color-emerald)] align-middle ml-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Wholesale</span>
              </span>
            </Link>
          </div>

          <h1 
            className="text-3xl text-white font-light text-center mb-8"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('login_title')}
          </h1>

          {redirectMessage && (
            <div
              className="mb-6 text-sm text-center p-3 bg-[color:rgb(var(--primary-rgb)/0.10)] border border-[color:rgb(var(--primary-rgb)/0.30)] text-[color:var(--color-primary)]"
              data-testid="login-redirect-message"
            >
              {redirectMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="input-label">{t('login_identifier')}</label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                className="w-full input-field"
                placeholder="+32 xxx xx xx xx / email@exemple.com"
                data-testid="login-phone"
              />
            </div>

            <div>
              <label className="input-label">{t('login_password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full input-field pr-12"
                  placeholder="••••••••"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className={`text-sm text-center p-3 ${
                errorType === 'pending' 
                  ? 'bg-amber-500/10 border border-amber-500/30 text-amber-400' 
                  : 'text-red-500'
              }`} data-testid="login-error">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between gap-3 -mt-2">
              <label className="flex items-center gap-2 cursor-pointer text-zinc-400 text-sm select-none">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-[color:var(--color-primary)]" data-testid="login-remember-checkbox" />
                {t('login_remember')}
              </label>
              <Link to="/mot-de-passe-oublie" className="text-zinc-500 hover:text-[color:var(--color-primary)] text-sm transition-colors" data-testid="forgot-password-link">
                {t('login_forgot')}
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? t('login_loading') : t('login_submit')}
            </button>
          </form>

          <AuthDivider />
          <GoogleSignInButton />

          <p className="text-center text-zinc-500 text-sm mt-6">
            {t('login_no_account')}{' '}
            <Link to="/inscription" className="text-[color:var(--color-primary)] hover:underline">
              {t('login_register')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
